import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { AuthContext } from '../../../context/AuthContext';
import '../../../index.css';
import { supabase } from '../../../utils/supabase';
import { getCourseGalleryImageSources } from '../../../utils/supabaseImageTransform';
import LoadingScreen from '../../loading/LoadingScreen';

/**
 * Horizontal photo strip: scroll-snap + optional dots (no react-slick).
 * All image URLs stay in the DOM as <img> — good for crawlers and prerender stability.
 */
function CoursePhotoGallery({ urls, courseName, courseImages }) {
  const scrollerRef = useRef(null);
  const [active, setActive] = useState(0);

  const onScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || !urls.length) return;
    const w = el.clientWidth || 1;
    const idx = Math.min(
      urls.length - 1,
      Math.max(0, Math.round(el.scrollLeft / w)),
    );
    setActive(idx);
  }, [urls.length]);

  const goTo = (index) => {
    const el = scrollerRef.current;
    if (!el) return;
    const w = el.clientWidth;
    el.scrollTo({ left: index * w, behavior: 'smooth' });
  };

  return (
    <div
      className="relative w-full"
      role="region"
      aria-label={`Photos of ${courseName}`}
    >
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="scrollbar-hide flex w-full scroll-smooth snap-x snap-mandatory overflow-x-auto overflow-y-hidden"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {urls.map((url, index) => {
          const { webpSrcSet, fallback } = getCourseGalleryImageSources(url);
          return (
          <div
            key={`${url}-${index}`}
            className="box-border w-full min-w-full shrink-0 snap-center snap-always px-2"
          >
            <div className="relative h-[250px] w-full md:h-[350px] lg:h-[492px]">
              {webpSrcSet ? (
                <picture className="absolute inset-0 block h-full w-full">
                  <source
                    type="image/webp"
                    srcSet={webpSrcSet}
                    sizes="100vw"
                  />
                  <img
                    className="absolute inset-0 block h-full w-full rounded-lg object-contain"
                    src={fallback}
                    alt={`${courseName} — gallery ${index + 1}`}
                    width={1280}
                    height={960}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                    fetchPriority={index === 0 ? 'high' : undefined}
                    sizes="100vw"
                  />
                </picture>
              ) : (
                <img
                  className="absolute inset-0 block h-full w-full rounded-lg object-contain"
                  src={fallback}
                  alt={`${courseName} — gallery ${index + 1}`}
                  width={1280}
                  height={960}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  fetchPriority={index === 0 ? 'high' : undefined}
                  sizes="100vw"
                />
              )}
              {index === 0 && courseImages?.credit && (
                <a
                  href={`https://golflists.com/profile/${courseImages.credit}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-3 left-1/2 right-auto z-10 -translate-x-1/2 transform rounded-full bg-black bg-opacity-60 px-3 py-1 text-xs text-white shadow-md transition hover:bg-opacity-80"
                  style={{ pointerEvents: 'auto' }}
                >
                  📸 {courseImages.credit}
                </a>
              )}
            </div>
          </div>
          );
        })}
      </div>
      {urls.length > 1 && (
        <div
          className="mt-2 flex justify-center gap-2"
          role="tablist"
          aria-label="Photo navigation"
        >
          {urls.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === active}
              aria-label={`View photo ${i + 1} of ${urls.length}`}
              className={`h-2.5 w-2.5 rounded-full transition-colors ${
                i === active
                  ? 'bg-indigo-600'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const CourseImageCarousel = ({ courseImages, course }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const { user, editableCourses, editPermissions, username } =
    useContext(AuthContext);
  const folderName = course.course_name.replace(/[^a-zA-Z0-9_-]/g, '_');
  const [courseImagesArray, setCourseImagesArray] = useState(
    courseImages?.course_image_array || [],
  );

  useEffect(() => {
    setCourseImagesArray(courseImages?.course_image_array || []);
  }, [courseImages]);
  const [showUploadDiv, setShowUploadDiv] = useState(false);
  const placeholderImageUrl =
    'https://api.golflists.com/storage/v1/object/public/brand/GL_LOGO_300_302.png?t=2024-01-23T11%3A40%3A04.181Z';
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const canUploadImage = () => {
    if (!user) return false;
    if (
      editPermissions === 'Advanced' ||
      editPermissions === 'Ambassador' ||
      editPermissions === 'Editor'
    )
      return true;
    if (
      editPermissions === 'Basic' &&
      Array.isArray(editableCourses) &&
      editableCourses.includes(course.course_name)
    )
      return true;
    return false;
  };

  const userCanUpload = canUploadImage();

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setSelectedFileName(file.name);
    }
  };

  async function uploadToStorage(file, folderName, supabase) {
    if (!file) {
      console.error('No file provided for upload.');
      return null;
    }

    setUploading(true);

    const timestamp = new Date().getTime();
    const fileExtension = file.name.split('.').pop();
    const uniqueFileName = `${file.name.split('.')[0]}_${timestamp}.${fileExtension}`;

    const { data, error } = await supabase.storage
      .from('course_images')
      .upload(`${folderName}/${uniqueFileName}`, file);

    console.log('data', data);

    setUploading(false);

    if (error) {
      alert(
        'The photo upload did not work, please refresh the page and try again.',
      );
      console.error('Error uploading file:', error);
      return null;
    }

    return `https://stjbqlfgvlefqghlyumg.supabase.co/storage/v1/object/public/course_images/${folderName}/${uniqueFileName}`;
  }

  const uploadImage = async () => {
    if (!canUploadImage()) {
      setError('You do not have permission to upload images.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const imageUrl = await uploadToStorage(
        selectedFile,
        folderName,
        supabase,
      );
      if (!imageUrl) {
        setError('Failed to upload image. Please try again.');
        return;
      }

      const updatedImageArray = [...courseImagesArray, imageUrl];

      const existingRow = await supabase
        .from('course_images')
        .select('*')
        .eq('course_name', course.course_name)
        .single();

      let updateOrInsertError;

      if (existingRow.data) {
        const { error } = await supabase
          .from('course_images')
          .update({ course_image_array: updatedImageArray })
          .eq('course_name', course.course_name);
        updateOrInsertError = error;
      } else {
        const { error } = await supabase.from('course_images').insert({
          course_uid: course.course_uid,
          course_name: course.course_name,
          slug_course_name: course.slug_course_name,
          course_image_array: updatedImageArray,
          username: username,
          external_link: `https://golflists.com/profile/${username}`,
          credit: username,
          caption: course.course_name,
          image_url: imageUrl,
          user_uid: user.id,
        });
        updateOrInsertError = error;
      }

      if (updateOrInsertError) {
        setError('Failed to update or create image. Please try again.');
        console.error(
          'Error updating or creating course images:',
          updateOrInsertError,
        );
      } else {
        setCourseImagesArray(updatedImageArray);
        setUploadSuccess(true);
        setTimeout(() => setUploadSuccess(false), 5000);
      }
    } catch (error) {
      setError('An error occurred during the upload. Please try again.');
      console.error('Upload failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleUploadDiv = () => {
    setShowUploadDiv(!showUploadDiv);
  };

  const hasImages = courseImagesArray && courseImagesArray.length > 0;

  const uploadWidgetStyles =
    'flex flex-col flex-nowrap justify-center items-center mx-auto absolute w-full h-full top-0 left-0 right-0 bg-white rounded-lg shadow-md px-4';

  if (!hasImages && !userCanUpload) {
    return (
      <>
        <section
          id="coursePhotos"
          className="min-h-[280px] md:min-h-[400px] lg:min-h-[575px] w-full bg-white mt-1 rounded-lg shadow mx-auto relative lg:px-2"
        >
          <article className="prose max-w-none prose-sm sm:prose-md text-left pt-6">
            <h2 className="hidden xl:block text-center truncate">
              Photos:{' '}
              <span className="text-indigo-700">{course.course_name}</span>
            </h2>
          </article>
          <img
            className="mx-auto rounded-lg w-auto h-[250px] md:min-h-[350px] lg:min-h-[492px]"
            src={placeholderImageUrl}
            alt={`${course.course_name} — gallery coming soon`}
          />
        </section>
      </>
    );
  }

  return isLoading ? (
    <LoadingScreen type="spin" color="#000" />
  ) : (
    <section
      id="coursePhotos"
      className="min-h-[280px] md:min-h-[400px] lg:min-h-[575px] w-full bg-white mt-1 rounded-lg shadow mx-auto relative lg:px-2"
    >
      {error && (
        <div className="text-red-500 text-center py-4">{error}</div>
      )}

      {hasImages ? (
        <>
          <article className="prose max-w-none prose-sm sm:prose-md text-left md:pt-2 xl:pt-6">
            <h2 className="hidden xl:block text-center truncate">
              Photos:{' '}
              <span className="text-indigo-700">{course.course_name}</span>
            </h2>
          </article>
          <CoursePhotoGallery
            urls={courseImagesArray}
            courseName={course.course_name}
            courseImages={courseImages}
          />
        </>
      ) : (
        <>
          <article className="prose max-w-none prose-sm sm:prose-md text-left pt-6">
            <h2 className="hidden xl:block text-center truncate">
              Photos:{' '}
              <span className="text-indigo-700">{course.course_name}</span>
            </h2>
          </article>
          <img
            className="mx-auto rounded-lg w-auto h-[250px] md:min-h-[350px] lg:min-h-[492px]"
            src={placeholderImageUrl}
            alt={`${course.course_name} — add your first upload`}
          />
        </>
      )}

      {userCanUpload && (
        <button
          onClick={toggleUploadDiv}
          className="min-w-[200px] absolute top-2 lg:top-16 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-blue-500 hover:bg-blue-700 text-white font-semibold rounded-lg"
          style={{ zIndex: 10 }}
          type="button"
        >
          <FontAwesomeIcon icon={solid('upload')} className="mr-2" />
          {showUploadDiv ? 'Hide' : 'Upload Image'}
        </button>
      )}

      {showUploadDiv && (
        <div className={uploadWidgetStyles}>
          <p className="text-center text-sm pt-8 xl:pt-0">
            Course photos can be uploaded by verified members of this golf course
            and GolfLists Ambassadors.
          </p>
          <div className="flex flex-col items-center justify-center space-y-2">
            <label
              htmlFor="fileInput"
              className="cursor-pointer min-w-[200px] text-center bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
            >
              Select Image
            </label>
            <input
              id="fileInput"
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleFileChange}
            />
            <span className="text-sm text-gray-600">
              {selectedFileName || 'No Image Selected'}
            </span>
            {uploadSuccess && (
              <div className="text-green-500 text-center py-4 w-full">
                Photo uploaded successfully!
              </div>
            )}
            <button
              onClick={uploadImage}
              disabled={uploading}
              className="min-w-[200px] bg-green-500 px-4 py-2 text-white rounded-md hover:bg-green-600"
              type="button"
            >
              {uploading ? 'Uploading...' : 'Upload Image'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default CourseImageCarousel;
